import React, { CSSProperties, useEffect, useState } from "react"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  Box,
  InputAdornment,
  IconButton,
  FormHelperText,
  FormControl,
  LinearProgress,
  Menu,
  MenuItem,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import { useAuthUser } from "react-auth-kit"
import Papa from "papaparse"
import Snackbar from "@mui/material/Snackbar"
import MuiAlert from "@mui/material/Alert"
import { AutoAwesome, Visibility, VisibilityOff } from "@mui/icons-material"
import { useDropzone } from "react-dropzone"
import VoterTable from "../../../components/VoterTable"
import PasswordField from "../../../components/PasswordField"
import api from "../../../utils/api"

interface Voter {
  id: string
  password: string
  username: string
  email: string
  name: string
}

interface FileDropzoneProps {
  onFileAccepted: (files: File[]) => void
  selectedFile: File | null
}

const FileDropzone: React.FC<FileDropzoneProps> = ({
  onFileAccepted,
  selectedFile,
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "text/file": [".csv"],
    },
    onDropAccepted: onFileAccepted,
    maxFiles: 1,
    multiple: false,
  })

  const dropzoneStyle: CSSProperties = {
    border: isHovered ? "2px dashed #007bff" : "2px dashed #cccccc",
    borderRadius: "5px",
    padding: "20px",
    textAlign: "center",
    color: isHovered ? "#007bff" : "#cccccc",
    cursor: "pointer",
    width: "100%",
    height: "150px",
  }

  return (
    <div
      {...getRootProps()}
      style={dropzoneStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <input {...getInputProps()} />
      {isDragActive ? (
        <p>Drop the file here ...</p>
      ) : (
        <p>
          {selectedFile
            ? `Selected file: ${selectedFile.name}`
            : "Drag 'n' drop a file here, or click to select a file"}
        </p>
      )}
    </div>
  )
}

interface SnackbarInfo {
  open: boolean
  severity: "info" | "error" | "success" | "warning"
  message: string
}

function CircularProgressWithLabel(props: any) {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress variant="determinate" {...props} />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="caption" component="div" color="textSecondary">
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  )
}

export const ManageVoterPage = () => {
  const [voters, setVoters] = useState<Voter[]>([])
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [editedVoter, setEditedVoter] = useState<Voter | null>(null)
  const [addVoter, setAddVoter] = useState<Voter | null>(null)
  const [name, setName] = useState("")
  const [username, setUsername] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false)
  const [voterToDelete, setVoterToDelete] = useState<Voter | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [voterLimit, setVoterLimit] = useState<number>(0)
  const [isDeleteAllConfirmationOpen, setIsDeleteAllConfirmationOpen] =
    useState(false)
  const [deleteAllSnackbarOpen, setDeleteAllSnackbarOpen] = useState(false)
  const [deletingAllVoters, setDeletingAllVoters] = useState<boolean>(false)
  const [snackbarInfo, setSnackbarInfo] = useState<SnackbarInfo>({
    open: false,
    severity: "info",
    message: "",
  })
  const [showPassword, setShowPassword] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const [isFileUploadDialogOpen, setIsFileUploadDialogOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const auth = useAuthUser()
  const eventId = auth()?.eventId?._path?.segments[1] ?? "null"

  useEffect(() => {
    ;(async () => {
      setIsLoading(true)

      // Fetching voters using Axios
      try {
        const response = await api.get(`/admin/events/${eventId}/voters`)
        setVoters(response.data)
      } catch (error) {
        console.error("Error fetching active voters:", error)
        setVoters([]) // Or handle the error as needed
      } finally {
        setIsLoading(false)
      }

      // Fetching voter limit using native fetch API
      try {
        const response = await api.get(`/admin/events/${eventId}`)
        setVoterLimit(Number(response.data.numVoters))
      } catch (error) {
        console.error("Error fetching Voter Limit:", error)
        openSnackbar("Error fetching Voter Limit", "error")
      }
    })()
  }, [eventId])

  // Function to open the snackbar with custom message and severity
  const openSnackbar = (
    message: string,
    severity: "info" | "error" | "success" | "warning" = "info"
  ) => {
    setSnackbarInfo({ open: true, message, severity })
  }

  const closeSnackbar = () => {
    setSnackbarInfo((prev) => ({ ...prev, open: false }))
  }

  const handleOpenEditModal = (voter: Voter) => {
    setEditedVoter(voter)
    setEmail(voter.email)
    setUsername(voter.username)
    setName(voter.name)
    //setPassword(voter.password)
    // setId(voter.id)
    setIsEditModalOpen(true)
  }

  const handleOpenAddModal = () => {
    setIsAddModalOpen(true)
  }

  const handleCloseAddModal = () => {
    setAddVoter(null)
    setIsAddModalOpen(false)
  }

  const handleCloseEditModal = () => {
    //setPassword("")
    setEditedVoter(null)
    setIsEditModalOpen(false)
  }

  const isUsernameAvailable = (username: string) => {
    if (voters.length === undefined) {
      voters.length = 1
      return true
    }
    const trimmedUsername = username.trim()
    let isAvailable = true

    for (const voter of voters) {
      if (voter.username.trim() === trimmedUsername) {
        isAvailable = false
        break // Exit the labeled loop
      }
    }
    return isAvailable
  }

  const isUsernameAvailableForUpdate = (
    oldUsername: string,
    username: string
  ) => {
    if (!voters || voters.length === 0) {
      return true
    }

    const trimmedUsername = username.trim()

    for (const voter of voters) {
      const existingTrimmedUsername = voter.username.trim()
      if (
        existingTrimmedUsername === trimmedUsername &&
        existingTrimmedUsername !== oldUsername.trim()
      ) {
        return false
      }
    }

    return true
  }

  //This is to check csv for duplicate usernames
  const hasDuplicateUsernames = (rows: CsvVoterRow[]): string | null => {
    const usernames = new Set()
    for (let i = 1; i < rows.length; i++) {
      const [, username] = rows[i]
      const trimmedUsername = username.trim()
      if (usernames.has(trimmedUsername)) {
        return trimmedUsername // Return the duplicate username
      }
      usernames.add(trimmedUsername)
    }

    return null // Return null if no duplicates are found
  }

  const handleUpdateVoter = async () => {
    if (!editedVoter) return
    if (!name || !username) {
      return
    }

    // Check if none of the fields have changed
    if (
      username === editedVoter.username &&
      name === editedVoter.name &&
      email === editedVoter.email &&
      password === ""
    ) {
      handleCloseEditModal()
      openSnackbar("Everything is up to date!", "info")
      return
    }

    // Check if the username is available and not equal to the current username
    if (isUsernameAvailableForUpdate(editedVoter.username, username)) {
      const updatedVoter = {
        ...editedVoter,
        name,
        email,
        username,
      }
      if (password !== "") {
        if (!isValidPassword(password)) {
          openSnackbar("Password doesn't meet minimum criteria.", "error")
          console.error("Invalid password.")
          return
        } else {
          updatedVoter.password = password
          setPassword("")
        }
      }

      try {
        await api.put(
          `/admin/events/${editedVoter.id}/edit-voter`,
          password ? updatedVoter : { ...updatedVoter, password: undefined }
        )

        // Update the state to reflect the edited voter:
        handleCloseEditModal()

        setVoters((prevVoters) => {
          return prevVoters.map((voter) => {
            if (voter.id === editedVoter.id) {
              return updatedVoter
            }
            return voter
          })
        })

        // Close the modal and show a success message
        handleCloseEditModal()
        openSnackbar("Voter has been updated.", "success")
      } catch (error) {
        openSnackbar("Error updating voter.", "error")
        console.error("Error updating voter:", error)
      }
    } else {
      openSnackbar("This username is already taken.", "error")
      console.error("Username already exists.")
    }
  }

  const handleAddVoter = async () => {
    if (!name || !username || !password) {
      return
    }

    // Check if email is valid
    // if (!/^\S+@\S+\.\S+$/.test(email)) {
    //   openSnackbar("Invalid email address. Missing @ or .", "error")
    //   console.error("Invalid email address.")
    //   return
    // }

    if (!isValidPassword(password)) {
      openSnackbar("Password doesn't meet minimum criteria.", "error")
      console.error("Invalid password.")
      return
    }

    const responseVoterLimit = await api.get(`/admin/events/${eventId}`)
    const voterLimitData = responseVoterLimit.data
    const voterLimit = voterLimitData.numVoters

    //compares the length of the array of voters in an event with the voter limit in that event
    if (voters.length === voterLimit) {
      openSnackbar("Max Voter Limit!", "error")

      return
    }

    const newVoter = {
      name,
      password,
      email,
      username,
    }

    if (isUsernameAvailable(newVoter.username)) {
      if (voters.length < voterLimit) {
        try {
          const response = await api.post(
            `/admin/events/${eventId}/voters`,
            newVoter
          )

          handleCloseAddModal()
          setTimeout(() => {
            window.location.reload()
          }, 2000)

          const createdVoter = response.data

          setVoters((prevVoters) => {
            //If previous voters is not an array or is null, initialize it as an empty array
            const votersArray = Array.isArray(prevVoters) ? prevVoters : []
            openSnackbar("Voter has been added successfully.", "success")
            return [...votersArray, createdVoter.voter]
          })
        } catch (error) {
          console.error("Error Adding voter:", error)
        }
      } else {
        openSnackbar(
          "This Voter Limit for this event has been reached.",
          "error"
        )
        console.error("Voter limit reached.")
      }
    } else {
      openSnackbar("This username is already taken.", "error")
      console.error("Username already exists")
    }
  }

  // valid password test
  const isValidPassword = (password: string) => {
    const regex = /^(?=.*[0-9])(?=.*[!@#$%^&*]).{6,}$/
    return regex.test(password)
  }

  const handleAddVotersFromCSV = async (votersData: Voter[]) => {
    // Filter out invalid data
    const validVoters = votersData.filter(
      (voter) => voter.name && voter.username && voter.password
    )

    if (validVoters.length === 0) {
      return []
    }

    try {
      const response = await api.post(
        `/admin/events/${eventId}/bulk-voters`,
        validVoters
      )

      const createdVoters = response.data
      return createdVoters.voters // Assuming the backend returns an array of added voters
    } catch (error) {
      console.error("Error adding voters:", error)
      return []
    }
  }

  type CsvVoterRow = [string, string, string, string]

  // Function to handle CSV file selection
  const handleCsvFileSelection = async (file: File) => {
    if (!file) return

    Papa.parse(file, {
      skipEmptyLines: true,
      complete: async (result) => {
        const rows = result.data as CsvVoterRow[]
        const numberOfVotersInCsv = rows.length - 1
        const batchSize = 25 // affects the progress bar
        const batches = Math.ceil(rows.length / batchSize)
        const allNewVoters: any = []
        let invalidPasswordCount = 0

        if (voters.length + numberOfVotersInCsv > voterLimit) {
          openSnackbar(
            `Uploading these voters will exceed the voter limit of ${voterLimit}.`,
            "error"
          )
          setTimeout(() => window.location.reload(), 2000)
          return
        }

        const duplicateUsername = hasDuplicateUsernames(rows)
        if (duplicateUsername) {
          openSnackbar(
            `Duplicate username found in the CSV: ${duplicateUsername}`,
            "error"
          )
          setTimeout(() => window.location.reload(), 2000)
          return
        }

        const usernameColumnIndex = 1
        //csv uniqueness
        for (let i = 1; i < rows.length; i++) {
          const usernameToCheck = rows[i][usernameColumnIndex]

          // Check if the username is unique
          const isUnique = isUsernameAvailable(usernameToCheck)

          if (!isUnique) {
            openSnackbar(
              `Username already exists: ${rows[i][usernameColumnIndex]}`,
              "error"
            )
            return
          }
        }

        setUploadProgress(1)

        openSnackbar(
          `Adding ${numberOfVotersInCsv} voters. This may take a few minutes...`,
          "info"
        )

        for (let i = 0; i < batches; i++) {
          const batchStart = i * batchSize + 1
          const batchEnd = Math.min(batchStart + batchSize, rows.length)
          const batchRows = rows.slice(batchStart, batchEnd)

          const votersData = batchRows
            .filter((row) => {
              const [, , , password] = row.map((value) => value.trim())
              const isValid = isValidPassword(password)
              if (!isValid) {
                invalidPasswordCount++
              }
              return isValid
            })
            .map((row) => {
              const [name, username, email, password] = row.map((value) =>
                value.trim()
              )
              return {
                name,
                username: username.replace(/\s/g, ""),
                email,
                password,
                id: eventId,
              }
            })

          const newVoters = await handleAddVotersFromCSV(votersData)
          allNewVoters.push(...newVoters)

          // Calculate and update the progress based on the current batch
          const currentProgress = ((i + 1) / batches) * 100

          // Update the progress
          setUploadProgress(currentProgress)
        }

        // Update the voters state only after all batches are processed
        setVoters((prevVoters) => [...prevVoters, ...allNewVoters])

        // Reset the progress bar and potentially trigger a UI update
        setUploadProgress(0)

        // Show a success message
        openSnackbar("Voters have been added successfully.", "success")

        // Refresh the page
        setTimeout(() => window.location.reload(), 3000) // temp fix

        if (invalidPasswordCount > 0) {
          openSnackbar(
            `${invalidPasswordCount} users could not be uploaded due to password requirements.`,
            "error"
          )
        }
      },
      header: false,
    })
  }

  const handleDeleteAll = async () => {
    setDeletingAllVoters(true)

    const batchSize = 25
    const votersToDelete = [...voters] // Create a copy of voters to avoid modifying the state directly

    while (votersToDelete.length > 0) {
      const batch = votersToDelete.splice(0, batchSize) // Get the next batch of voters to delete

      try {
        // Create an array of promises to delete voters in parallel
        const deletePromises = batch.map(async (voter) => {
          await api.delete(`/admin/events/${voter.id}/delete-voter`)
        })

        await Promise.all(deletePromises) // Wait for all voters in the batch to be deleted
      } catch (error) {
        console.error("Error deleting voters:", error)
      }
    }

    setVoters([])
    setIsDeleteAllConfirmationOpen(false)
    setDeleteAllSnackbarOpen(true)

    setDeletingAllVoters(false)
  }

  const handleDeleteVoter = (voter: Voter) => {
    setVoterToDelete(voter)
    setIsDeleteConfirmationOpen(true)
  }

  const confirmDeleteVoter = async () => {
    if (!voterToDelete) return

    try {
      await api.delete(`/admin/events/${voterToDelete.id}/delete-voter`)

      setVoters((prevVoters) =>
        prevVoters.filter((voter) => voter.id !== voterToDelete.id)
      )
      setIsDeleteConfirmationOpen(false)
      openSnackbar("Voter has been deleted.", "info")
    } catch (error) {
      console.error("Error deleting voter:", error)
    }
  }

  const handleMenuClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleManualEntry = () => {
    handleMenuClose()
    handleOpenAddModal()
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleFileUpload = () => {
    handleMenuClose()
    setIsFileUploadDialogOpen(true)
  }

  const handleFileChange = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleUploadClick = () => {
    if (selectedFile) {
      handleCsvFileSelection(selectedFile)
      setIsFileUploadDialogOpen(false)
    } else {
      openSnackbar("No file selected.", "error")
    }
  }

  const downloadTemplate = () => {
    const link = document.createElement("a")
    link.href = "/LoadVoter.csv"
    link.download = "LoadVoter.csv"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    openSnackbar("Template has been downloaded", "success")
  }

  return (
    <>
      <Typography variant="h6" m={4}>
        Manage Voters
      </Typography>

      {isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <Box sx={{ mb: 2 }}>
          {/* What's in the box???? */}
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between", // Maintains space between main containers
              alignItems: "center", // Vertically center elements
              width: "100%",
              marginBottom: "1rem",
            }}
          >
            {/* Container for Progress Bar (aligned to the far left) */}
            <div
              style={{ display: "flex", justifyContent: "flex-start", flex: 1 }}
            >
              {uploadProgress > 0 && uploadProgress < 100 && (
                <CircularProgressWithLabel value={uploadProgress} />
              )}
            </div>

            {/* Container for Delete All and Add Buttons (aligned to the far right) */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                flexShrink: 0,
              }}
            >
              <Button
                variant="contained"
                style={{ backgroundColor: "red", marginRight: "8px" }}
                aria-label="Delete All"
                onClick={() => setIsDeleteAllConfirmationOpen(true)}
                disabled={voters.length === 0}
              >
                Delete All
              </Button>
              <Button
                variant="contained"
                aria-label="Add"
                onClick={handleMenuClick}
                style={{ backgroundColor: "#008000" }}
                aria-controls={open ? "add-menu" : undefined}
                aria-haspopup="true"
                disabled={voters.length === voterLimit || uploadProgress > 0}
              >
                <AddIcon />
              </Button>

              <Menu
                id="add-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                MenuListProps={{
                  "aria-labelledby": "add-button",
                }}
              >
                <MenuItem onClick={handleFileUpload}>File Upload</MenuItem>
                <MenuItem onClick={handleManualEntry}>Manual Entry</MenuItem>
              </Menu>
            </div>
          </Box>

          <VoterTable
            voters={voters}
            onEdit={handleOpenEditModal}
            onDelete={handleDeleteVoter}
          />
        </Box>
      )}

      {/* Start of editing voter pop up */}
      <Dialog open={isEditModalOpen} onClose={handleCloseEditModal}>
        <DialogTitle>Edit Voter</DialogTitle>
        <DialogContent>
          {editedVoter && (
            <form>
              <TextField
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
                fullWidth
                required
                sx={{ marginY: 2 }}
              />
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
                sx={{ marginY: 2 }}
              />
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.replace(/\s/g, ""))}
                fullWidth
                sx={{ marginY: 2 }}
              />
              <FormControl fullWidth sx={{ marginY: 2 }}>
                <TextField
                  label="Password"
                  variant="outlined"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) =>
                    setPassword(e.target.value.replace(/\s/g, ""))
                  }
                  fullWidth
                  margin="normal"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FormHelperText sx={{ color: "red" }}>
                  If you prefer to keep the current password, leave this field
                  blank.
                </FormHelperText>
              </FormControl>
            </form>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseEditModal}
            color="secondary"
            variant="contained"
          >
            Cancel
          </Button>

          <Button
            onClick={handleUpdateVoter}
            color="primary"
            variant="contained"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
      {/* End of editing voter pop up */}

      {/* Start of Add Voter pop up  */}
      <Dialog open={isAddModalOpen} onClose={handleCloseAddModal}>
        <DialogTitle>Add Voter</DialogTitle>
        <DialogContent>
          {
            <form>
              <TextField
                label="Username"
                onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
                fullWidth
                required
                sx={{ marginY: 2 }}
              />
              <TextField
                label="Name"
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
                sx={{ marginY: 2 }}
              />
              <TextField
                label="Email"
                type="email"
                onChange={(e) => setEmail(e.target.value.replace(/\s/g, ""))}
                fullWidth
                sx={{ marginY: 2 }}
              />
              <PasswordField
                password={password}
                setPassword={setPassword}
                generatePassword={generatePassword}
              />
            </form>
          }
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseAddModal}
            color="secondary"
            variant="contained"
          >
            Cancel
          </Button>

          <Button onClick={handleAddVoter} color="primary" variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>
      {/* End of Add voter */}

      {/* Start of Delete voter pop up */}
      <Dialog
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this voter?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDeleteConfirmationOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={confirmDeleteVoter} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start of Delete All Dialog */}
      <Dialog
        open={isDeleteAllConfirmationOpen}
        onClose={() => {
          setIsDeleteAllConfirmationOpen(false)
          setDeletingAllVoters(false) // Reset the state when closing the dialog
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          {deletingAllVoters ? (
            <div>
              <Typography>
                Please wait, this may take a couple of minutes...
              </Typography>
              <LinearProgress />
            </div>
          ) : (
            <Typography>Are you sure you want to delete all voters?</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsDeleteAllConfirmationOpen(false)
              setDeletingAllVoters(false) // Reset the state when canceling
            }}
            color="primary"
          >
            Cancel
          </Button>
          {!deletingAllVoters && (
            <Button
              onClick={async () => {
                setDeletingAllVoters(true)
                await handleDeleteAll()
                setIsDeleteAllConfirmationOpen(false)
                setDeletingAllVoters(false) // Reset the state after deletion
              }}
              color="primary"
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Start of File Upload Dialog */}
      <Dialog
        open={isFileUploadDialogOpen}
        onClose={() => setIsFileUploadDialogOpen(false)}
        PaperProps={{
          style: { width: "80%", maxWidth: "600px", height: "auto" },
        }}
      >
        <DialogTitle>Upload a CSV file to add voters</DialogTitle>
        <DialogContent>
          <div>
            <FileDropzone
              onFileAccepted={handleFileChange}
              selectedFile={selectedFile}
            />
          </div>
          <div style={{ marginTop: "1rem" }}>
            <Typography>
              Download the{" "}
              <span
                onClick={downloadTemplate}
                style={{
                  cursor: "pointer",
                  textDecoration: "underline",
                  color: "#007bff",
                  fontWeight: "bold",
                }}
              >
                template file
              </span>{" "}
              to see the required format.
            </Typography>
          </div>
        </DialogContent>
        <DialogActions
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "1rem",
          }}
        >
          <Button
            onClick={() => setIsFileUploadDialogOpen(false)}
            color="secondary"
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadClick}
            style={{ backgroundColor: "#008000" }}
            variant="contained"
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={deleteAllSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setDeleteAllSnackbarOpen(false)}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setDeleteAllSnackbarOpen(false)}
          style={{ backgroundColor: "green", color: "#ffffff" }}
          severity="success"
        >
          All voters have been deleted.
        </MuiAlert>
      </Snackbar>

      {/*new snackbar */}
      <Snackbar
        open={snackbarInfo.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={closeSnackbar}
          severity={snackbarInfo.severity}
        >
          {snackbarInfo.message}
        </MuiAlert>
      </Snackbar>
    </>
  )
}

function generatePassword() {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz"
  const numbers = "0123456789"
  const specialChars = "!@#$%^&*"

  const pickRandom = (set: string) =>
    set.charAt(Math.floor(Math.random() * set.length))

  return (
    pickRandom(chars) +
    pickRandom(numbers) +
    pickRandom(specialChars) +
    Array.from({ length: Math.floor(Math.random() * 8) + 3 }, () =>
      pickRandom(chars + numbers + specialChars)
    ).join("")
  )
}
